import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaDollarSign, FaPrint, FaSave } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import CommissionDetailsModal from "./CommissionDetailsModal";
import {
  PROGRESS_REPORT_COLUMNS,
  PROGRESS_REPORT_FILTER_KEYS,
  applyProgressReportFilters,
  buildProgressReportRows,
  cloneProgressReportRows,
  getProgressReportColumnLabel,
  getProgressReportFilterOptions,
  PROGRESS_REPORT_TYPEABLE_FILTER_KEYS,
  PROGRESS_REPORT_SEARCHABLE_FILTER_KEYS,
  buildProgressReportTradeFilterOptions,
  buildProgressReportAddressFilterOptions,
  getProgressReportColumnCellClass,
  getProgressReportBlankSelectOptions,
  getProgressReportSelectOptions,
  isProgressReportSelectColumn,
  PROGRESS_REPORT_BLANK_SELECT_KEYS,
  progressReportDateForInput,
  progressReportDateFromInput,
} from "../utils/progressReportUtils";
import { saveProgressReportRows } from "../utils/progressReportSave";
import { openProgressReportPrintWindowFromRows } from "../utils/progressReportPrintHtml";

const EDITABLE_KEYS = new Set([
  "tradeCategory",
  "compliance",
  "action",
  "notes",
  "followUpDate",
  "invoiceDate",
]);

const cellSelectClass =
  "w-full pl-2 pr-8 py-1.5 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none cursor-pointer truncate";

const cellSelectMinWidth = {
  tradeCategory: "min-w-[7rem]",
  compliance: "min-w-[5.5rem]",
};

const tradeFilterSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "34px",
    fontSize: "0.75rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.75rem", zIndex: 80 }),
  menuPortal: (base) => ({ ...base, zIndex: 80 }),
};

const cellInputClass =
  "w-full min-w-[4.5rem] pl-2 pr-2 py-1.5 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";

const cellDateClass =
  "w-full min-w-[9rem] pl-2 pr-8 py-1.5 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";

const cellInputMinWidth = {
  action: "min-w-0",
  notes: "min-w-0",
};

function ReportCell({ col, row, rows, onChange, readOnly }) {
  const value = row[col.key] ?? "";

  if (readOnly || !EDITABLE_KEYS.has(col.key)) {
    const display =
      col.key === "status"
        ? getProgressReportSelectOptions("status", rows)?.find(
            (o) => o.value === value,
          )?.label || value
        : value;
    return (
      <span
        className={`block px-2 py-1.5 text-xs text-gray-800 ${
          col.key === "agent" || col.key === "outsideBroker" || col.key === "address"
            ? "truncate"
            : "whitespace-nowrap"
        }`}
        title={display || undefined}
      >
        {display || "—"}
      </span>
    );
  }

  if (col.edit === "select" || isProgressReportSelectColumn(col.key)) {
    const strValue = String(value ?? "").trim();
    if (PROGRESS_REPORT_BLANK_SELECT_KEYS.has(col.key)) {
      const options =
        getProgressReportBlankSelectOptions(col.key, strValue) || [];
      return (
        <select
          required
          value={strValue}
          onChange={(e) => onChange(row.rowKey, col.key, e.target.value)}
          className={`${cellSelectClass} ${cellSelectMinWidth[col.key] || "min-w-[5rem]"} ${
            !strValue
              ? "invalid:text-transparent [&>option]:text-gray-800"
              : ""
          }`}
          title={strValue || undefined}
        >
          <option value="" disabled hidden />
          {options.map((opt) => (
            <option key={`${col.key}-${opt.value}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    const options = getProgressReportSelectOptions(col.key, rows) || [];
    return (
      <select
        value={strValue}
        onChange={(e) => onChange(row.rowKey, col.key, e.target.value)}
        className={`${cellSelectClass} ${cellSelectMinWidth[col.key] || "min-w-[5rem]"}`}
        title={strValue || undefined}
      >
        {options.map((opt) => (
          <option key={`${col.key}-${opt.value}`} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  if (col.edit === "date" || col.key === "followUpDate" || col.key === "invoiceDate") {
    const iso = progressReportDateForInput(value);
    return (
      <input
        type="date"
        value={iso}
        onChange={(e) =>
          onChange(
            row.rowKey,
            col.key,
            progressReportDateFromInput(e.target.value),
          )
        }
        className={cellDateClass}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(row.rowKey, col.key, e.target.value)}
      className={`${cellInputClass} ${cellInputMinWidth[col.key] || ""}`}
      title={value || undefined}
    />
  );
}

const ProgressReportModal = ({
  isOpen,
  onClose,
  trades,
  loading,
  onTradesSaved,
}) => {
  const { isBroker } = useRole();
  const readOnly = Boolean(isBroker);
  const [rows, setRows] = useState([]);
  const [baselineRows, setBaselineRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterColumn, setFilterColumn] = useState("");
  const [saving, setSaving] = useState(false);
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setRows([]);
      setBaselineRows([]);
      setFilters({});
      setFilterColumn("");
      return;
    }
    const built = buildProgressReportRows(trades || []);
    const cloned = cloneProgressReportRows(built);
    setRows(cloned);
    setBaselineRows(cloneProgressReportRows(built));
  }, [isOpen, trades]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const filteredRows = useMemo(
    () => applyProgressReportFilters(rows, filters),
    [rows, filters],
  );

  const tradeFilterOptions = useMemo(
    () => buildProgressReportTradeFilterOptions(trades),
    [trades],
  );

  const addressFilterOptions = useMemo(
    () => buildProgressReportAddressFilterOptions(rows),
    [rows],
  );

  const selectedTradeFilterOption = useMemo(() => {
    const q = String(filters.tradeNumber || "").trim();
    if (!q) return null;
    return (
      tradeFilterOptions.find((o) => o.value === q) ||
      tradeFilterOptions.find((o) =>
        String(o.value).toLowerCase().includes(q.toLowerCase()),
      ) ||
      null
    );
  }, [tradeFilterOptions, filters.tradeNumber]);

  const selectedAddressFilterOption = useMemo(() => {
    const q = String(filters.address || "").trim();
    if (!q) return null;
    return (
      addressFilterOptions.find((o) => o.value === q) || { value: q, label: q }
    );
  }, [addressFilterOptions, filters.address]);

  const handleCellChange = useCallback((rowKey, columnKey, value) => {
    setRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey ? { ...row, [columnKey]: value } : row,
      ),
    );
  }, []);

  const handleFilterColumnChange = (columnKey) => {
    setFilterColumn(columnKey);
    if (!columnKey) return;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  const filterUsesSearchInput =
    filterColumn && PROGRESS_REPORT_SEARCHABLE_FILTER_KEYS.has(filterColumn);

  const searchableFilterOptions =
    filterColumn === "address" ? addressFilterOptions : tradeFilterOptions;

  const selectedSearchFilterOption =
    filterColumn === "address"
      ? selectedAddressFilterOption
      : selectedTradeFilterOption;

  const handleFilterValueChange = (value) => {
    if (!filterColumn) return;
    setFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[filterColumn];
      else next[filterColumn] = value;
      return next;
    });
  };

  const handleSearchFilterInputChange = (inputValue, meta) => {
    if (filterColumn !== "address" || meta.action !== "input-change") return;
    handleFilterValueChange(inputValue);
  };

  const removeFilter = (columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    if (filterColumn === columnKey) setFilterColumn("");
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!rows.length) {
      toast.error("No rows to save.");
      return;
    }
    setSaving(true);
    try {
      const { dirtyCount, savedCount } = await saveProgressReportRows(
        rows,
        baselineRows,
        trades,
        axiosInstance,
      );
      if (dirtyCount === 0) {
        toast.info("No changes to save.");
        return;
      }
      if (savedCount === 0) {
        toast.error(
          "Changes could not be saved. Restart the backend and try again.",
        );
        return;
      }
      if (onTradesSaved) await onTradesSaved();
      toast.success(
        `Saved ${savedCount} trade${savedCount === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to save progress report changes.");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!filteredRows.length) {
      toast.error("No rows to print.");
      return;
    }
    const opened = openProgressReportPrintWindowFromRows(filteredRows);
    if (!opened) {
      toast.error("Pop-up blocked. Allow pop-ups to print the report.");
    }
  };

  if (!isOpen) return null;

  const filterActiveCount = Object.values(filters).filter(
    (v) => v != null && String(v).trim() !== "",
  ).length;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="progress-report-modal-title"
        className="bg-white rounded-xl shadow-2xl w-full max-w-[min(98vw,1600px)] max-h-[min(92vh,900px)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2
              id="progress-report-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Progress Report
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Edit Trade Category, Compliance, Action, Notes, Follow Up Date,
              and Invoice Date here. Other dates come from Progress Tracker.
              Filter Trade # or Address using the searchable list. Save updates
              your edits on each trade.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        {!loading && rows.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100 shrink-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-xs font-medium text-gray-600">Filter</p>
              <button
                type="button"
                onClick={() => setCommissionModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-900 text-xs font-medium text-white hover:bg-blue-950 shrink-0"
              >
              
                Commission Details
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-0.5 min-w-[10rem]">
                <span className="text-[10px] font-medium text-gray-500">
                  Column
                </span>
                <select
                  value={filterColumn}
                  onChange={(e) => handleFilterColumnChange(e.target.value)}
                  className="rounded border border-gray-300 text-xs py-1.5 pl-2 pr-7 bg-white min-w-[10rem]"
                >
                  <option value="">Select column…</option>
                  {PROGRESS_REPORT_FILTER_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {getProgressReportColumnLabel(key)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5 min-w-[12rem] flex-1 max-w-md">
                <span className="text-[10px] font-medium text-gray-500">
                  Value
                </span>
                {filterUsesSearchInput ? (
                  <Select
                    inputId={`progress-report-${filterColumn}-filter`}
                    options={searchableFilterOptions}
                    value={selectedSearchFilterOption}
                    onChange={(opt) =>
                      handleFilterValueChange(opt?.value || "")
                    }
                    onInputChange={handleSearchFilterInputChange}
                    isClearable
                    isSearchable
                    isDisabled={!filterColumn}
                    placeholder={
                      filterColumn === "address"
                        ? "Type or select an address…"
                        : "Search or select a trade…"
                    }
                    noOptionsMessage={() =>
                      filterColumn === "address"
                        ? "No matching addresses"
                        : "No matching trades"
                    }
                    styles={tradeFilterSelectStyles}
                    className="w-full text-xs"
                    classNamePrefix={`progress-report-${filterColumn}-filter`}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                  />
                ) : (
                  <select
                    value={filterColumn ? filters[filterColumn] || "" : ""}
                    onChange={(e) =>
                      handleFilterValueChange(e.target.value)
                    }
                    disabled={!filterColumn}
                    className="rounded border border-gray-300 text-xs py-1.5 pl-2 pr-7 bg-white w-full disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {!filterColumn ? (
                      <option value="">Select a column first</option>
                    ) : (
                      getProgressReportFilterOptions(filterColumn, rows).map(
                        (opt) => (
                          <option
                            key={`${filterColumn}-${opt.value}`}
                            value={opt.value}
                          >
                            {opt.label}
                          </option>
                        ),
                      )
                    )}
                  </select>
                )}
              </label>
            </div>
            {filterActiveCount > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {Object.entries(filters)
                  .filter(([, v]) => v != null && String(v).trim() !== "")
                  .map(([key, value]) => (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs text-blue-900"
                    >
                      <span className="font-medium">
                        {getProgressReportColumnLabel(key)}:
                      </span>
                      <span>
                        {key === "tradeNumber"
                          ? selectedTradeFilterOption?.label ||
                            tradeFilterOptions.find((o) => o.value === value)
                              ?.label ||
                            value
                          : key === "address"
                            ? selectedAddressFilterOption?.label || value
                            : value}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFilter(key)}
                        className="ml-0.5 text-blue-700 hover:text-blue-950 leading-none"
                        aria-label={`Remove ${getProgressReportColumnLabel(key)} filter`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                <button
                  type="button"
                  onClick={() => {
                    setFilters({});
                    setFilterColumn("");
                  }}
                  className="text-xs text-blue-800 hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto px-4 py-3 min-h-0">
          {loading ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              Loading trades…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              No trades to show.
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="text-sm text-gray-500 py-12 text-center">
              No trades match the selected filters.
            </p>
          ) : (
            <table className="min-w-full table-fixed border border-gray-300 text-xs [&_select]:bg-[length:12px] [&_select]:bg-[right_0.35rem_center] [&_select]:bg-no-repeat [&_select]:[background-image:url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E')] [&_select]:appearance-none">
              <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
                <tr>
                  {PROGRESS_REPORT_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 whitespace-nowrap ${getProgressReportColumnCellClass(col.key)}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.rowKey} className="hover:bg-gray-50/80">
                    {PROGRESS_REPORT_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={`border border-gray-200 p-0 align-top overflow-hidden ${getProgressReportColumnCellClass(col.key)}`}
                      >
                        <ReportCell
                          col={col}
                          row={row}
                          rows={rows}
                          onChange={handleCellChange}
                          readOnly={readOnly}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 shrink-0 flex flex-wrap items-center justify-between gap-3 bg-gray-50 rounded-b-xl">
          <span className="text-sm text-gray-600">
            {filterActiveCount > 0
              ? `${filteredRows.length} of ${rows.length} trades`
              : `${rows.length} trade${rows.length === 1 ? "" : "s"}`}
          </span>
          <div className="flex flex-wrap gap-2">
            {readOnly ? null : (
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving || !rows.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-green-700 bg-green-700 text-white text-sm font-medium hover:bg-green-800 disabled:opacity-50"
            >
              <FaSave /> {saving ? "Saving…" : "Save"}
            </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || !filteredRows.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-900 text-white text-sm font-medium hover:bg-blue-950 disabled:opacity-50"
            >
              <FaPrint /> Print
            </button>
          </div>
        </div>
      </div>

      <CommissionDetailsModal
        isOpen={commissionModalOpen}
        onClose={() => setCommissionModalOpen(false)}
        trades={trades}
        loadingTrades={loading}
      />
    </div>
  );
};

export default ProgressReportModal;
